import { useParams } from "react-router-dom";

import { Box, Popover, Tooltip } from "@mui/material";
import { useState } from "react";
import CustomDataGridV2 from "../../../../../../../../components/CustomDataGridV2";
import Flex from "../../../../../../../../components/Flex";
import StatusChip from "../../../../../../../../components/StatusChip";
import Text from "../../../../../../../../components/Text";
import ActionMenu from "../../../../../../../../components/app/ActionMenu";
import useDisclosure from "../../../../../../../../hooks/use-disclosure";
import { useGetAllEligibilityByPatientID } from "../../../../../../../../queries/eligibility/use-get-all-eligibility-by-patientID";
import { COMPONENT } from "../../../../../../../../tokens/design-tokens";
import {
  formatDate,
  formatDateTime,
} from "../../../../../../../../utils/date-time-utility";
import {
  DEF_PAGINATION,
  DGT_Pagination,
  applyDefaultColumnSettings,
  getXtenantId,
  replaceUnderScoreAddSpace,
} from "../../../../../../../../utils/utils";
import EligibilityResponseModal from "./eligibilityResponseModal";

// Eligibility status colors pulled from design-tokens.ts (Figma: Badge/Text + Badge/Bg)
const ELIGIBILITY_STATUS_COLOR_MAP: Record<
  string,
  { color: string; bgColor: string }
> = {
  ACTIVE: {
    color: COMPONENT.badge.text.positive,
    bgColor: COMPONENT.badge.bg.positive,
  },
  INACTIVE: {
    color: COMPONENT.badge.text.neutral,
    bgColor: COMPONENT.badge.bg.neutral,
  },
  PENDING: {
    color: COMPONENT.badge.text.warning,
    bgColor: COMPONENT.badge.bg.warning,
  },
  EXPIRED: {
    color: COMPONENT.badge.text.negative,
    bgColor: COMPONENT.badge.bg.negative,
  },
};

/**
 * Collect the distinct service types from a row's benefits. Each
 * `benefit.serviceType` can itself be a comma-separated list, so we split,
 * trim and de-duplicate while preserving the order in which they appear.
 */
const getServiceTypes = (row: any): string[] => {
  const benefits: any[] = row?.benefits ?? [];
  const types = benefits.flatMap((b) =>
    (b?.serviceType ?? "")
      .split(",")
      .map((s: string) => s.trim())
      .filter(Boolean)
  );
  return Array.from(new Set<string>(types));
};

/**
 * Render the first service type as a chip and reveal the remaining ones in a
 * hover popover (mirrors the Speciality cell pattern in NewTemplateTable).
 */
const ServiceTypeCell = ({ types }: { types: string[] }) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const open = Boolean(anchorEl);

  if (types.length === 0) return <>-</>;

  const [first, ...rest] = types;

  return (
    <Flex
      gap={0.5}
      alignItems="center"
      sx={{ width: "100%", minWidth: 0, overflow: "hidden" }}
    >
      <StatusChip
        variant="info"
        label={first}
        sx={{
          maxWidth: rest.length > 0 ? 130 : "100%",
          "& .MuiChip-label": {
            overflow: "hidden",
            textOverflow: "ellipsis",
            display: "block",
          },
        }}
      />
      {rest.length > 0 && (
        <>
          <Text
            onMouseEnter={(e) => setAnchorEl(e.currentTarget)}
            onMouseLeave={() => setAnchorEl(null)}
          >
            +{rest.length}
          </Text>
          <Popover
            id="service-type-popover"
            sx={{ pointerEvents: "none" }}
            open={open}
            anchorEl={anchorEl}
            anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
            transformOrigin={{ vertical: "top", horizontal: "left" }}
            onClose={() => setAnchorEl(null)}
            disableRestoreFocus
          >
            <Flex flexWrap="wrap" width={280} gap={0.5} p={0.8}>
              {rest.map((t) => (
                <StatusChip key={t} variant="info" label={t} />
              ))}
            </Flex>
          </Popover>
        </>
      )}
    </Flex>
  );
};

const PatientEligibility = () => {
  const [pagination, setPagination] = useState<DGT_Pagination>(DEF_PAGINATION);

  const eligibilityResponseDisclosure = useDisclosure(false);

  const {
    data: eligibilityData,
    isLoading: isEligibilityLoading,
    isFetching: isEligibilityFetching,
  } = useGetAllEligibilityByPatientID({
    patientId: useParams()?.patientId ?? undefined,
    xTENANTID: getXtenantId(),
    pagination: pagination,
  });

  const isVisitNotePage = window.location.pathname.includes("/visit-note");
  const MINIMIZED_ELIGIBILITY_FIELDS = new Set([
    "payerName",
    "coverageStatus",
    "effectiveStartDate",
    "action",
  ]);

  const COLUMNS = applyDefaultColumnSettings([
    {
      field: "payerName",
      flex: 0.4,
      minWidth: 150,
      headerName: "Payer Name",
      renderCell: ({ row }: any) => {
        const payerName = row?.payer?.name || "-";
        return (
          <Tooltip title={payerName} placement="top">
            <Box
              sx={{
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                width: "100%",
              }}
            >
              <Text isTabletext>{payerName}</Text>
            </Box>
          </Tooltip>
        );
      },
    },
    {
      field: "policyNumber",
      flex: 0.4,
      minWidth: 150,
      headerName: "Policy / Member No.",
      renderCell: ({ row }: any) => (
        <Text isTabletext>{row?.policyNumber || "-"}</Text>
      ),
    },
    {
      field: "coverageStatus",
      flex: 0.4,
      minWidth: 120,
      headerName: "Coverage Status",
      renderCell: ({ row }: any) => {
        const status = row?.coverageStatus;
        if (!status) return "-";
        const colors = ELIGIBILITY_STATUS_COLOR_MAP[status.toUpperCase()] ?? {};
        return (
          <StatusChip
            label={replaceUnderScoreAddSpace(status.toLowerCase())}
            color={colors?.color}
            bgColor={colors?.bgColor}
          />
        );
      },
    },
    {
      field: "effectiveStartDate",
      flex: 0.4,
      minWidth: 130,
      headerName: "Coverage Start Date",
      renderCell: ({ row }: any) =>
        row?.effectiveStartDate ? formatDate(row.effectiveStartDate) : "-",
    },
    {
      field: "effectiveEndDate",
      flex: 0.4,
      minWidth: 130,
      headerName: "Coverage End Date",
      renderCell: ({ row }: any) =>
        row?.effectiveEndDate ? formatDate(row.effectiveEndDate) : "-",
    },
    {
      field: "eligibilityType",
      flex: 0.4,
      minWidth: 180,
      headerName: "Type",
      sortable: false,
      renderCell: ({ row }: any) => (
        <ServiceTypeCell types={getServiceTypes(row)} />
      ),
    },
    {
      field: "created",
      flex: 0.4,
      minWidth: 170,
      headerName: "Checked On",
      renderCell: ({ row }: any) =>
        row?.created ? formatDateTime(row.created) : "-",
    },
    {
      field: "transactionId",
      flex: 0.4,
      minWidth: 140,
      headerName: "Transaction ID",
      renderCell: ({ row }: any) => (
        <Text isTabletext>{row?.transactionId || "-"}</Text>
      ),
    },
    {
      field: "action",
      flex: 0.3,
      minWidth: 80,
      headerName: "Action",
      sortable: false,
      renderCell: ({ row }: any) => (
        <ActionMenu
          options={[{ value: "view", label: "View" }]}
          onActionClick={(v) => {
            if (v === "view") {
              eligibilityResponseDisclosure.open(row);
            }
          }}
        />
      ),
    },
  ]);

  const filteredColumns = isVisitNotePage
    ? COLUMNS.filter((col) => MINIMIZED_ELIGIBILITY_FIELDS.has(col.field))
    : COLUMNS;

  return (
    <>
      {eligibilityResponseDisclosure.opened && (
        <EligibilityResponseModal
          disclosureHost={eligibilityResponseDisclosure}
        />
      )}
      <Box
        sx={{
          height: "100%",
          width: "100%",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <CustomDataGridV2
          loading={isEligibilityLoading || isEligibilityFetching}
          getRowId={(row) => row.uuid}
          rows={eligibilityData?.data?.content ?? []}
          columns={filteredColumns}
          disableRowSelectionOnClick
          rowCount={eligibilityData?.data?.page?.totalElements ?? 10}
          paginationModel={pagination}
          onPaginationModelChange={setPagination}
          // onSortModelChange={setGridSorting}
        />
      </Box>
    </>
  );
};

export default PatientEligibility;
